import { Injectable } from '@angular/core';
import { BaseDownloadServiceWithFilter } from './base.service';
import { Client, CommandResult, Document, DocumentFilter } from '@cl/shared/models';
import { HttpClient, HttpParams } from '@angular/common/http';
import { NzImageService } from 'ng-zorro-antd/image';
import { lastValueFrom } from 'rxjs';
import { FileMetadata } from '@cl/shared/models/file.model';
import { DocumentFile } from '@cl/shared/models/documents/document-file.model';
import { BaseClientNotificationConfiguration } from '@cl/shared/models/base/base-client-notification-configuration.model';
import { DashboardDocumentStats } from '@cl/shared/models/documents/dashboard-document-stats.model';
import { AddSignerToMultipleDocumentFilesDto } from '@cl/shared/models/documents/add-signer-to-multiple-document-files.model';
import { EmployeeDocumentNotification } from '@cl/shared/models/documents/employee-document-notification.model';
import { DocumentFileSigner } from '@cl/shared/models/documents/document-file-signer.model';
import { MassDocumentFileImportResult } from '@cl/shared/models/documents/mass-document-file-import-result.model';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';

@Injectable({
    providedIn: 'root',
})
export class DocumentsService extends BaseDownloadServiceWithFilter<Document, DocumentFilter> {
    constructor(
        protected httpClient: HttpClient,
        protected nzImageService: NzImageService,
        protected messageService: NzMessageService,
        protected modalService: NzModalService,
    ) {
        super(httpClient, '/api/v1/documents/', nzImageService);
    }

    async downloadDocumentFile(downloadDocumentFileParams: HttpParams): Promise<void> {
        return this.handleFileDownloading(
            lastValueFrom(
                this.httpClient.get<CommandResult<FileMetadata>>(`${this.baseApiUrl}download-file`, { params: downloadDocumentFileParams }),
            ),
        );
    }

    async getDocumentFileMetadata(documentId: number, documentFileId: number, entryName?: string): Promise<CommandResult<FileMetadata>> {
        let params = new HttpParams().set('documentId', documentId).set('documentFileId', documentFileId);
        if (entryName !== undefined) {
            params = params.set('entryName', entryName);
        }

        return lastValueFrom(this.httpClient.get<CommandResult<FileMetadata>>(`${this.baseApiUrl}download-file`, { params }));
    }

    async downloadAllDocumentFiles(documentId: number): Promise<void> {
        return this.handleFileDownloading(
            lastValueFrom(this.httpClient.get<CommandResult<FileMetadata>>(`${this.baseApiUrl}download-all-files/${documentId}`)),
        );
    }

    async sendDocumentSigningNotification(clientNotificationConfig: BaseClientNotificationConfiguration): Promise<CommandResult<void>> {
        return await lastValueFrom(this.httpClient.post<CommandResult<void>>(`${this.baseApiUrl}send-notification`, clientNotificationConfig));
    }

    async updateDocumentFile(documentFile: DocumentFile): Promise<CommandResult<void>> {
        return await lastValueFrom(this.httpClient.put<CommandResult<void>>(`${this.baseApiUrl}document-file`, documentFile));
    }

    async sendEmployeeDocumentSigningNotification(employeeDocumentNotificationOptions: EmployeeDocumentNotification): Promise<void> {
        return await lastValueFrom(this.httpClient.post<void>(`${this.baseApiUrl}send-employee-notification`, employeeDocumentNotificationOptions));
    }

    async getDocumentClient(documentId: number): Promise<CommandResult<Client>> {
        return await lastValueFrom(this.httpClient.get<CommandResult<Client>>(`${this.baseApiUrl}${documentId}/client`));
    }

    async addDocumentFile(documentFileFormData: FormData): Promise<void> {
        return await lastValueFrom(this.httpClient.post<void>(`${this.baseApiUrl}document-file`, documentFileFormData));
    }

    async deleteDocumentFile(documentId: number, documentFileId: number): Promise<void> {
        return await lastValueFrom(this.httpClient.delete<void>(`${this.baseApiUrl}document-file/${documentId}/${documentFileId}`));
    }

    async getDocumentFile(documentId: number, documentFileId: number): Promise<CommandResult<DocumentFile>> {
        return await lastValueFrom(
            this.httpClient.get<CommandResult<DocumentFile>>(`${this.baseApiUrl}document-file/${documentId}/${documentFileId}`),
        );
    }

    async getCommonDocumentFileSigners(commonDocumentFileSignersQueryParams: HttpParams): Promise<CommandResult<DocumentFileSigner[]>> {
        return await lastValueFrom(
            this.httpClient.get<CommandResult<DocumentFileSigner[]>>(`${this.baseApiUrl}common-signers`, {
                params: commonDocumentFileSignersQueryParams,
            }),
        );
    }

    async addSignerToMultipleDocumentFiles(addSignerToMultipleDocumentFilesModel: AddSignerToMultipleDocumentFilesDto): Promise<CommandResult<void>> {
        return await lastValueFrom(
            this.httpClient.post<CommandResult<void>>(
                `${this.baseApiUrl}add-signer-to-multiple-document-files`,
                addSignerToMultipleDocumentFilesModel,
            ),
        );
    }

    async deleteSignerFromMultipleDocumentFiles(deleteSignerFromMultipleDocumentFilesParams: HttpParams): Promise<CommandResult<void>> {
        return await lastValueFrom(
            this.httpClient.delete<CommandResult<void>>(`${this.baseApiUrl}delete-signer-form-multiple-document-files`, {
                params: deleteSignerFromMultipleDocumentFilesParams,
            }),
        );
    }

    async getDocumentStatsForDashboard(): Promise<CommandResult<DashboardDocumentStats>> {
        return await lastValueFrom(this.httpClient.get<CommandResult<DashboardDocumentStats>>(`${this.baseApiUrl}dashboard-stats`));
    }

    async massDocumentFileImport(documentFileImportFormData: FormData): Promise<CommandResult<MassDocumentFileImportResult>> {
        return await lastValueFrom(
            this.httpClient.post<CommandResult<MassDocumentFileImportResult>>(`${this.baseApiUrl}mass-import`, documentFileImportFormData),
        );
    }
}
